// =============================================================================
// gardeHistorique.js : le retour arrière ne ferme jamais une fenêtre de
// travail en silence (relecture du 04/10/2026, règle produit du 24/08/2026).
// -----------------------------------------------------------------------------
// Le retour arrière (bouton du navigateur, Alt+←, balayage d'iOS) changeait de
// vue sans passer par fermerFenetresDeTravail : une fenêtre <ModalShell>
// modifiée était démontée sans dialogue et sa saisie perdue, alors qu'un lien
// de notification attend déjà le dialogue de choix (differerSiFenetreOuverte).
//
// popstate ne dit ni le sens ni l'ampleur du saut (retour, avance, menu du
// bouton retour qui saute trois pages). Chaque entrée posée par navTo porte
// donc sa POSITION dans l'état d'historique : un fil (une suite d'entrées
// posées par cette mécanique, tiré au hasard) et un index dans ce fil.
// L'écart entre l'entrée quittée et l'entrée atteinte dit le sens et l'ampleur.
//
// Fenêtre de travail ouverte et écart connu :
//   1. l'historique est RAMENÉ sur l'entrée courante (history.go(-écart)) : la
//      vue ne bouge pas, l'adresse redevient celle de ce qui est affiché ;
//   2. chaque fenêtre passe par son dialogue de choix, du sommet vers le fond
//      (fermerFenetresDeTravail) ;
//   3. toutes fermées, le saut est REJOUÉ (history.go(écart)) et s'applique
//      comme un retour ordinaire ; une fenêtre gardée (« Annuler », garde
//      métier, échec de l'enregistrement) : on reste, fenêtre, saisie et
//      adresse comprises.
// Aucune entrée d'historique de plus (go ne fait que parcourir) ; aucune
// boucle : le popstate de la restauration est consommé sans rien appliquer, le
// rejeu ne part qu'une fois, après le dialogue. Un second retour pendant le
// dialogue est ramené de même, sans second dialogue.
//
// Défilement. Ramener l'historique fait enregistrer par le navigateur, pour
// l'entrée quittée, la position de défilement de la page AFFICHÉE (celle de la
// fenêtre, souvent plus courte) : y revenir ensuite (rejeu, ou retour ordinaire
// après « Annuler ») la rendait en haut de page, et Safari iOS ne repeignait
// plus l'en-tête en verre dépoli avant le premier défilement (banc du 04/10).
// La garde suit donc la position de chaque entrée (événement scroll, passif)
// et la rétablit après le rendu, de retour sur une entrée qu'elle a ramenée.
//
// Une entrée SANS position (posée avant ce lot, ou dont un autre code a écrasé
// l'état) garde le comportement d'avant : le saut s'applique tel quel.
// reecrireAdresse (parametresVue.js) et useParamVue (budget) recopient l'état
// avant de le récrire : la position de l'entrée survit.
// Verrou : src/retourArriereFenetre.test.js.
// =============================================================================
import { fenetresDeTravailOuvertes, fermerFenetresDeTravail } from "./uxComponents";

export const CLE_FIL = "aramisFil";
export const CLE_INDEX = "aramisIndex";
// Sans popstate de restauration passé ce délai, on est resté sur l'entrée
// atteinte : le dialogue s'ouvre quand même, et le saut s'applique sur place.
export const DELAI_RESTAURATION_MS = 1500;

const nouveauFil = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
const memePosition = (a, b) => !!a && !!b && a.fil === b.fil && a.index === b.index;

/**
 * Position d'une entrée d'historique, lue dans son état.
 * @param {*} etat  history.state ou PopStateEvent.state
 * @returns {{fil: string, index: number}|null}  null : entrée sans position
 */
export function positionDe(etat) {
  if (!etat || typeof etat !== "object") return null;
  const fil = etat[CLE_FIL];
  const index = etat[CLE_INDEX];
  return typeof fil === "string" && fil && Number.isInteger(index) ? { fil, index } : null;
}

/**
 * Écart entre l'entrée quittée et l'entrée atteinte : négatif pour un retour,
 * positif pour une avance. null quand on ne sait pas (position inconnue, fil
 * différent, même index), jamais 0 : history.go(0) recharge la page.
 * @returns {number|null}
 */
export function ecartEntre(depuis, vers) {
  if (!depuis || !vers || depuis.fil !== vers.fil) return null;
  const ecart = vers.index - depuis.index;
  return Number.isInteger(ecart) && ecart !== 0 ? ecart : null;
}

const lireDefilementFenetre = () => (typeof window !== "undefined" ? window.scrollY || window.pageYOffset || 0 : 0);
const defilerFenetre = (y) => { try { window.scrollTo(0, y); } catch { /* jsdom */ } };
const ecouterDefilementFenetre = (fn) => {
  try { window.addEventListener("scroll", fn, { passive: true }); } catch { /* sans fenêtre */ }
};
// Après le rendu de la vue : deux images (le navigateur a déjà posé SA position).
const apresRendu = (fn) => (typeof requestAnimationFrame === "function"
  ? requestAnimationFrame(() => requestAnimationFrame(fn))
  : setTimeout(fn, 32));

/**
 * La garde, une par historique. Les dépendances sont injectables (tests).
 * @param {{ historique?: function(): History, fenetresOuvertes?: function(): number,
 *   fermerFenetres?: function(string): Promise<boolean>, delaiRestauration?: number,
 *   lireDefilement?: function(): number, defiler?: function(number): void,
 *   ecouterDefilement?: function(function): void, planifier?: function(function): void }} [deps]
 */
export function creerGardeHistorique({
  historique = () => window.history,
  fenetresOuvertes = fenetresDeTravailOuvertes,
  fermerFenetres = fermerFenetresDeTravail,
  delaiRestauration = DELAI_RESTAURATION_MS,
  lireDefilement = lireDefilementFenetre,
  defiler = defilerFenetre,
  ecouterDefilement = ecouterDefilementFenetre,
  planifier = apresRendu,
} = {}) {
  let courante = null;   // position de l'entrée que la vue montre (null : inconnue)
  let attente = null;    // restauration demandée dont le popstate n'est pas arrivé
  let retenu = false;    // un saut retenu attend le dialogue de choix
  const defilements = new Map();   // "fil:index" -> défilement vertical de l'entrée
  const aRetablir = new Set();     // entrées dont le navigateur a perdu le défilement
  const cle = (p) => p.fil + ":" + p.index;
  const noterDefilement = () => { if (courante) defilements.set(cle(courante), lireDefilement()); };
  let suivi = false;
  const suivreDefilement = () => {
    if (suivi) return;
    suivi = true;
    // Pendant une restauration, la page affichée n'est pas celle de l'entrée
    // où se trouve le navigateur : rien n'est noté.
    ecouterDefilement(() => { if (!attente) noterDefilement(); });
  };

  // Ramène l'historique de `ecart` en sens inverse. Résout true au popstate de
  // la restauration, false s'il n'arrive pas (on est resté sur l'entrée atteinte).
  const restaurer = (ecart, cible) => new Promise((resolve) => {
    const a = { retour: courante, cible, resolve, minuteur: null };
    const abandon = () => { if (attente === a) attente = null; resolve(false); };
    a.minuteur = setTimeout(abandon, delaiRestauration);
    attente = a;
    try { historique().go(-ecart); } catch { clearTimeout(a.minuteur); abandon(); }
  });

  return {
    /** Position de l'entrée affichée (tests). */
    get positionCourante() { return courante; },

    /**
     * Montage : l'entrée courante reçoit une position si elle n'en a pas, et
     * `etat` (avec `url` si donnée) quand il diffère de l'état en place.
     * @param {object} etat
     * @param {string} [url]
     */
    adopter(etat, url) {
      const h = historique();
      const actuel = h.state;
      const ici = positionDe(actuel);
      const pos = ici || { fil: nouveauFil(), index: 0 };
      courante = pos;
      suivreDefilement();
      if (etat === actuel && ici) return;
      const voulu = { ...(etat || {}), [CLE_FIL]: pos.fil, [CLE_INDEX]: pos.index };
      if (url == null) h.replaceState(voulu, "");
      else h.replaceState(voulu, "", url);
    },

    /**
     * pushState d'une navigation : la nouvelle entrée suit l'entrée courante
     * dans son fil, ou ouvre un fil neuf si l'entrée courante n'a pas de
     * position. Une exception de pushState (Safari et ses plafonds) remonte à
     * l'appelant, la position n'avance pas.
     */
    pousser(etat, url) {
      const h = historique();
      const ici = positionDe(h.state);
      const pos = ici ? { fil: ici.fil, index: ici.index + 1 } : { fil: nouveauFil(), index: 0 };
      noterDefilement();
      h.pushState({ ...(etat || {}), [CLE_FIL]: pos.fil, [CLE_INDEX]: pos.index }, "", url);
      courante = pos;
    },

    /**
     * À brancher sur popstate. `appliquer(etat)` change la vue : appelé tout de
     * suite sans fenêtre ouverte (ou pour une entrée sans position), après le
     * dialogue de choix sinon, et jamais si une fenêtre est gardée.
     * @param {PopStateEvent} e
     * @param {function(object): void} appliquer
     */
    surPopstate(e, appliquer) {
      const etat = e ? e.state : null;
      const position = positionDe(etat);
      if (attente) {
        // Le popstate de NOTRE restauration : rien à appliquer. L'entrée d'où
        // l'on revient a reçu du navigateur le défilement de la page affichée.
        const a = attente;
        attente = null;
        clearTimeout(a.minuteur);
        courante = position || a.retour;
        if (a.cible) aRetablir.add(cle(a.cible));
        a.resolve(true);
        return;
      }
      const ecart = ecartEntre(courante, position);
      if (ecart !== null && fenetresOuvertes() > 0) {
        if (retenu) {
          // Second retour pendant le dialogue : ramené aussi, sans second dialogue.
          restaurer(ecart, position).then((ok) => { if (!ok) courante = position; });
          return;
        }
        retenu = true;
        const depart = courante;
        const liberer = () => { retenu = false; };
        restaurer(ecart, position)
          .then((restauree) => {
            if (!restauree) courante = position;
            return Promise.resolve(fermerFenetres("retour")).then((fermees) => {
              if (!fermees) return;
              if (!restauree) { appliquer(etat); return; }      // déjà sur l'entrée atteinte
              // Rejeu, sauf si une navigation a eu lieu pendant le dialogue.
              if (memePosition(courante, depart)) historique().go(ecart);
            });
          })
          .then(liberer, liberer);
        return;
      }
      courante = position;
      // Défilement d'une entrée ramenée : lu MAINTENANT (le navigateur va poser
      // le sien, faux, et l'événement scroll le noterait), rétabli après le rendu.
      const k = position && aRetablir.has(cle(position)) ? cle(position) : null;
      const y = k ? defilements.get(k) : undefined;
      appliquer(etat);
      if (k) {
        aRetablir.delete(k);
        if (typeof y === "number") planifier(() => defiler(y));
      }
    },
  };
}

/** La garde de l'historique de la fenêtre (App.jsx). */
export const gardeHistorique = creerGardeHistorique();
